// =============================================================================
// SimSense Platform — uPlot Chart Wrapper
// =============================================================================
// Cherry-picked from friend's project. Provides a React wrapper around uPlot
// with automatic resize handling via ResizeObserver.

import { useLayoutEffect, useRef, forwardRef, useImperativeHandle } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

type UPlotChartProps = {
  data: uPlot.AlignedData;
  options: uPlot.Options;
};

export type UPlotChartHandle = {
  setSize: (size: { width: number; height: number }) => void;
  toggleSeries?: (indices: number[], show: boolean) => void;
  setCursor?: (opts: { left: number; top: number }) => void;
  getUPlot: () => uPlot | null;
};

export const UPlotChart = forwardRef<UPlotChartHandle, UPlotChartProps>(
  ({ data, options }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const plotRef = useRef<uPlot | null>(null);

    useImperativeHandle(ref, () => ({
      setSize: (size) => plotRef.current?.setSize(size),
      toggleSeries: (indices, show) => {
        indices.forEach((i) => plotRef.current?.setSeries(i, { show }));
      },
      setCursor: (opts) => plotRef.current?.setCursor(opts),
      getUPlot: () => plotRef.current,
    }));

    useLayoutEffect(() => {
      if (!containerRef.current) return;

      plotRef.current = new uPlot(options, data, containerRef.current);
      const u = plotRef.current;

      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const w = entry.contentRect.width;
          const h = Math.max(entry.contentRect.height - 30, 200);
          u.setSize({ width: w, height: h });
        }
      });
      ro.observe(containerRef.current);

      return () => {
        ro.disconnect();
        u.destroy();
      };
    }, []); // create only once

    useLayoutEffect(() => {
      if (!plotRef.current) return;
      plotRef.current.setData(data);
    }, [data]);

    // height: 100% so the parent container fully controls pixel height
    return <div ref={containerRef} style={{ width: "100%", height: "100%" }} />;
  }
);
